import { graphql } from 'gatsby';
import * as React from "react"
import Layout from '~/components/Layout';
import FlexibleContent from '~/components/FlexibleContent';
import Seo from 'gatsby-plugin-wpgraphql-seo';
import CustomSEO from '~/components/seo/CustomSEO';

export default function defaultPage({ data: { wpPage } }) {

  // Pull the nested ACF group into one safe reference. This can be null
  // for a page that has no Page-group data configured, so everything
  // below must read off it with optional chaining.
  const pageData = wpPage?.page?.page;

  const content = pageData?.content;

  // Match on __typename first (stable), fall back to fieldGroupName
  // (older/alternate plugin versions) so this doesn't silently break
  // if one or the other is missing.
  const heroBlock = content?.find(
    (block) =>
      block?.__typename === 'WpPage_Page_Page_Content_Hero' ||
      block?.fieldGroupName === 'page_Page_Page_Content_Hero'
  );
  const heroTwoColumnBlock = content?.find(
    (block) =>
      block?.__typename === 'WpPage_Page_Page_Content_HeroTwoColumn' ||
      block?.fieldGroupName === 'page_Page_Page_Content_HeroTwoColumn'
  );

  const hero = heroBlock?.hero;
  const heroTwoColumn = heroTwoColumnBlock?.heroTwoColumn;

  const customSeo = {
    ...wpPage?.seo,
    image: hero?.image ? hero?.image : heroTwoColumn?.image,
  }

  return (
    <Layout
      headerWhite={pageData?.whiteHeader ?? false}
      backgroundColour={pageData?.backgroundColour}
      showLogin={pageData?.showLogin}
      footerCtaType={pageData?.footerCtaType}
      footerResourcesType={pageData?.footerResourcesType}
      pageLanguage={pageData?.pageLanguage}
    >
      <Seo post={wpPage} />
      <CustomSEO seo={customSeo} />
      <FlexibleContent content={pageData?.content} />
    </Layout>
  )
}

export const defaultPageQuery = graphql`
  query defaultPagePageQuery($id: String!) {
    wpPage(id: { eq: $id }) {
      title
      id
      ...SEO
      page {
        page {
          backgroundColour
          whiteHeader
          showLogin
          footerCtaType
          footerResourcesType
          pageLanguage
          content {
            ... on WpPage_Page_Page_Content_TwoColumns {
              __typename
              fieldGroupName
              twoColumns {
                fieldGroupName
                alignImage
                backgroundColour {
                  colour
                  colourSize
                  alignColour
                  padding {
                    top
                    bottom
                  }
                }
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText
                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                image {
                  ... Image
                }
                secondImage {
                  ... Image
                }
                thirdImage {
                  ... Image
                }
                alignSecondImage
              }
            }
            ... on WpPage_Page_Page_Content_Testimonial {
              __typename
              fieldGroupName
              testimonial {
                fieldGroupName
                backgroundColour {
                  colour
                  colourSize
                  alignColour
                  padding {
                    top
                    bottom
                  }
                }
                quote
                fieldGroupName
                author {
                  role
                  name
                  fieldGroupName
                }
                hidetrustpilotwidget
                quoteTagType
                link {
                  ... Button
                }
                innerBackgroundColour
                alignImage
                image {
                  ... Image
                }
              }
            }
            ... on WpPage_Page_Page_Content_Features {
              __typename
              fieldGroupName
              features {
                fieldGroupName
                layout
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                features {
                  content
                  fieldGroupName
                  heading
                  image {
                    ... Image
                  }
                  link {
                    ... Button
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_FullWidthImage {
              __typename
              fieldGroupName
              image {
                ... Image
              }
            }
            ... on WpPage_Page_Page_Content_Reviews {
              __typename
              fieldGroupName
              reviews {
                fieldGroupName
                heading
                reviewsData{
                review
                stars
                name
                date
                image {
                  ...Image
                }
                }
                # type
                # rating
                # count
              }
            }
            ... on WpPage_Page_Page_Content_Faqs {
              __typename
              fieldGroupName
              faqs {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                faqs {
                  question
                  fieldGroupName
                  answer
                }
                link {
                  ... Button
                }
              }
            }
            ... on WpPage_Page_Page_Content_Usps {
              __typename
              fieldGroupName
              usps {
                fieldGroupName
                design
                backgroundColour {
                  colour
                  colourSize
                  alignColour
                  padding {
                    top
                    bottom
                  }
                }
                showArrows
                design
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText
                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                usps {
                  alignText
                  content
                  heading
                  fieldGroupName
                  imageSize
                  link {
                    ... Button
                  }
                  image {
                    ... Image
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_Team {
              __typename
              fieldGroupName
              team {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                members {
                  image {
                    ... Image
                  }
                  name
                  title
                  linkedinUrl
                }
              }
            }
            ... on WpPage_Page_Page_Content_Press {
              __typename
              fieldGroupName
              press {
                fieldGroupName
                press {
                  heading
                  link {
                    ... Button
                  }
                  logo {
                    ... Image
                  }
                }
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_LatestResources {
              __typename
              fieldGroupName
              latestResources {
                fieldGroupName
                resourceType
                customResources {
                  resource {
                    ... on WpPost {
                      id
                      uri
                      title
                      excerpt
                      featuredImage {
                        node {
                          ...Image
                        }
                      }
                    }
                  }
                  
                }
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_Intro {
              __typename
              fieldGroupName
              intro {
                fieldGroupName
                subheading
                heading
                button {
                  ... Button
                }
              }
            }
            ... on WpPage_Page_Page_Content_Hero {
              __typename
              fieldGroupName
              hero {
                fieldGroupName
                image {
                  ... Image
                }
                video {
                  ... Video
                }
                fieldGroupName
                form
                showHelpFeatures
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                isHomepage
                language
              }
            }
            ... on WpPage_Page_Page_Content_TwoColumnFeaturesWImage {
              __typename
              fieldGroupName
              twoColumnFeaturesWImage {
                fieldGroupName
                image {
                  ... Image
                }
                backgroundColour {
                  colourSize
                  alignColour
                  colour
                  padding {
                    top
                    bottom
                  }
                }
                features {
                  content
                  heading
                  illustration {
                    ... Image
                  }
                }
                button {
                  button {
                    button {
                      ... Button
                    }
                  }
                  type
                }
                alignImage
                imagePadding
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_Awards {
              __typename
              fieldGroupName
              awards {
                fieldGroupName
                title
                subTitle
                backgroundImage {
                  ... Image
                }
                awards {
                  year
                  image {
                    ... Image
                  }
                  heading
                  fieldGroupName
                }
              }
            }

            ... on WpPage_Page_Page_Content_Keepintouch {
              __typename
              fieldGroupName
              keepintouch {
                fieldGroupName
                title
                subtitle
                image {
                  ... Image
                }
              }
            }

            ... on WpPage_Page_Page_Content_Buildings {
              __typename
              fieldGroupName
              buildings {
                fieldGroupName
              }
            }
            ... on WpPage_Page_Page_Content_Longletfees {
              __typename
              fieldGroupName
              longletfees {
                fieldGroupName
              }
            }
            ... on WpPage_Page_Page_Content_Cta {
              __typename
              fieldGroupName
              cta {
                fieldGroupName
                illustrationSize
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                illustration {
                  ... Image
                }
                fieldGroupName
              }
            }
            ... on WpPage_Page_Page_Content_Careers {
              __typename
              fieldGroupName
              careers {
                fieldGroupName
                subheading
                careers {
                  title
                  department
                  externalUrl
                  location {
                    city
                    country
                  }
                }
                fieldGroupName
              }
            }
            ... on WpPage_Page_Page_Content_Values {
              __typename
              fieldGroupName
              values {
                fieldGroupName
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                values {
                  content
                  fieldGroupName
                  heading
                  illustration {
                    ... Image
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_TestimonialsSlider {
              __typename
              fieldGroupName
              testimonialsSlider {
                fieldGroupName
                cursorBackgroundColour
                margin {
                  top
                  bottom
                }
                testimonials {
                  testimonial {
                    quote
                    image {
                      ... Image
                    }
                    author {
                      name
                      role
                    }
                    hidetrustpilotwidget
                    quoteTagType
                    link {
                      ... Button
                    }
                    innerBackgroundColour
                    alignImage
                  }
                  fieldGroupName
                }
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                fieldGroupName
              }
            }
            ... on WpPage_Page_Page_Content_TwoColumnFeatures {
              __typename
              fieldGroupName
              twoColumnFeatures {
                fieldGroupName
                features {
                  content
                  heading
                  illustration {
                    ... Image
                  }
                }
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_TextImageGrid {
              __typename
              fieldGroupName
              textImageGrid {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                images {
                  image {
                    ... Image
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_Logos {
              __typename
              fieldGroupName
              logos {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                logos {
                  logo {
                    ... Image
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_Quote {
              __typename
              fieldGroupName
              quote {
                fieldGroupName
                backgroundColour {
                  colourSize
                  alignColour
                  colour
                  padding {
                    top
                    bottom
                  }                }
                quote
                caption
                author {
                  name
                  role
                }
                illustration {
                  ... Image
                }
              }
            }
            ... on WpPage_Page_Page_Content_HeroPricing {
              __typename
              fieldGroupName
              heroPricing {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                language
              }
            }
            ... on WpPage_Page_Page_Content_HeroReferral {
              __typename
              fieldGroupName
              heroReferral {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_HeroPricing2 {
              __typename
              fieldGroupName
              heroPricing2 {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_PricingWidget {
              __typename
              fieldGroupName
              pricingWidget {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText
                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                simpletext
                simpletextSecond
                simpletextThird
              }
            }
            ... on WpPage_Page_Page_Content_IntroWImagesParallaxing {
              __typename
              fieldGroupName
              introWImagesParallaxing {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                images {
                  image {
                    ... Image
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_HeroSlider {
              __typename
              fieldGroupName
              heroSlider {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                images {
                  image {
                    ... Image
                  }
                  location
                }
              }
            }
            ... on WpPage_Page_Page_Content_IntroImages {
              __typename
              fieldGroupName
              introImages {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                desktopImage {
                  ... Image
                }
                mobileImage {
                  ... Image
                }
              }
            }
            ... on WpPage_Page_Page_Content_HeroTwoColumn {
              __typename
              fieldGroupName
              heroTwoColumn {
                fieldGroupName
                backgroundColour
                showArrow
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                image {
                  ... Image
                }
              }
            }
            ... on WpPage_Page_Page_Content_ImageSlider {
              __typename
              fieldGroupName
              imageSlider {
                fieldGroupName
                images {
                  image {
                    ... Image
                  }
                }
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_ImageUsps {
              __typename
              fieldGroupName
              imageUsps {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                usps {
                  heading
                  content
                  image {
                    ... Image
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_HeroAbout {
              __typename
              fieldGroupName
              heroAbout {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
                illustration {
                  ... Image
                }
                image {
                  ... Image
                }
              }
            }
            ... on WpPage_Page_Page_Content_TextCta {
              __typename
              fieldGroupName
              textCta {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                  buttons {
                    button {
                      button {
                        button {
                          ... Button
                        }
                      }
                      type
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_PartnerStory {
              __typename
              fieldGroupName
              featuredStory {
                fieldGroupName
                alignImage
                story {
                  ... on WpPartnerStory {
                    id
                    uri
                    title
                    excerpt
                    featuredImage {
                      node {
                        ...Image
                      }
                    }
                    partnerStoryItem {
                      partnerStoryItem {
                        stat {
                          content
                          heading
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_Newsletter {
              __typename
              fieldGroupName
              newsletter {
                fieldGroupName
                illustrationSize
                heading
                content
                illustration {
                  ... Image
                }
              }
            }
            ... on WpPage_Page_Page_Content_Neighbourhood {
              __typename
              fieldGroupName
              neighbourhood {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                }
                neighbourhoodImages {
                  neighbourhoodImages {
                    image {
                      ... Image
                    }
                    heading
                    fieldGroupName
                  }
                }
              }
            }
            ... on WpPage_Page_Page_Content_Certificate {
              __typename
              fieldGroupName
              paymentsTenantsTemplate {
                fieldGroupName
                textCard {
                  illustration {
                    ... Image
                  }
                  subheading
                  subheadingColor
                  alignText

                  heading {
                    text
                    type
                  }
                  content
                }
                simpletext
                textTitle
                containersLogoText {
                  containerText
                  containerLogo {
                    ... Image
                  }
                  linkButton {
                    ... Button
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`